import ChartView from 'echarts/lib/view/Chart';
import type GlobalModel from 'echarts/lib/model/Global';
import type ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import * as graphic from 'echarts/lib/util/graphic';
import RoamController, { RoamControllerHost } from 'echarts/lib/component/helper/RoamController';
import SeriesData from 'echarts/lib/data/SeriesData';
import Tree from 'echarts/lib/data/Tree';
import { getInstanceById } from 'echarts/lib/core/echarts';
import { Payload } from 'echarts/lib/echarts.all';
import { Group } from 'echarts/lib/util/graphic';
import { BoundingRect, Line, Polygon, Rect, Text } from 'zrender';
import type {
  RelationshipDiagramSeriesModelClazz as RelationshipDiagramSeriesModel,
  RelationTreeNode
} from './RelationshipDiagramSeries';
import type RoamView from '../../component/roam/RoamView';
import { createEl } from './util';

type PropsType = (Line | Polygon | Rect | Text) & {
  type: string;
};
type NodeInfo = {
  x: number;
  y: number;
} & {
  [key: string]: PropsType;
};

// eslint-disable-next-line no-use-before-define
export type RelationshipDiagramViewClazz = RelationshipDiagramView & ChartView;

// 通用绘制元素方法
function drawEl(nodeInfo: NodeInfo, group: graphic.Group) {
  Object.keys(nodeInfo).forEach(name => {
    const childOption = nodeInfo[name];
    const { type, ...props } = childOption;
    const childEl = createEl(childOption as { type: string });
    if (!childEl) {
      return;
    }

    childEl.attr(props);
    childEl.name = name;

    group.add(childEl);
  });
}

// 绘制同级分支线
function drawLevelLine(mainGroup: graphic.Group, tree: Tree) {
  const group = new graphic.Group({ name: 'levelLine' });
  const { data } = tree;
  const sameLevelLine = data.getLayout('levelLine') as NodeInfo[];
  if (!sameLevelLine) {
    return;
  }

  sameLevelLine.forEach((line: NodeInfo, index) => {
    const lineGroup = new graphic.Group({ name: `line-${index}`, x: line.x, y: line.y });
    drawEl(line, lineGroup);
    group.add(lineGroup);
  });
  mainGroup.add(group);
}

// 绘制导向线
function drawToNextTreeLine(mainGroup: graphic.Group, tree: Tree) {
  const group = new graphic.Group({ name: 'toNextTreeLine' });
  const { data } = tree;
  const toNextTreeLineLayout = data.getLayout('toNextTreeLine') as NodeInfo[];
  if (!toNextTreeLineLayout) {
    return;
  }

  toNextTreeLineLayout.forEach((toNextTreeLineRect: NodeInfo, index: number) => {
    if (!toNextTreeLineRect) {
      return;
    }
    const toNextTreeLineGroup = new graphic.Group({
      name: `toNextTreeLine-${index}`,
      x: toNextTreeLineRect.x,
      y: toNextTreeLineRect.y
    });
    drawEl(toNextTreeLineRect, toNextTreeLineGroup);

    group.add(toNextTreeLineGroup);
  });
  mainGroup.add(group);
}

// 绘制水印
function drawWatermark(mainGroup: graphic.Group, tree: Tree) {
  const group = new graphic.Group({ name: 'watermark' });
  const { data } = tree;
  const watermarkLayout = data.getLayout('watermark') as NodeInfo[];
  if (!watermarkLayout) {
    return;
  }

  watermarkLayout.forEach((watermarkRect: NodeInfo, index: number) => {
    const watermarkGroup = new graphic.Group({
      name: `watermark-${index}`,
      x: watermarkRect.x,
      y: watermarkRect.y
    });
    drawEl(watermarkRect, watermarkGroup);

    group.add(watermarkGroup);
  });
  mainGroup.add(group);
}

// 绘制背景颜色
function drawBackgroundColor(mainGroup: graphic.Group, tree: Tree) {
  const group = new graphic.Group({ name: 'background' });
  const { data } = tree;
  const backgroundRectLayout = data.getLayout('background') as NodeInfo[];
  if (!backgroundRectLayout) {
    return;
  }

  backgroundRectLayout.forEach((backgroundRect: NodeInfo, index: number) => {
    const backgroundGroup = new graphic.Group({
      name: `background-${index}`,
      x: backgroundRect.x,
      y: backgroundRect.y
    });
    drawEl(backgroundRect, backgroundGroup);

    group.add(backgroundGroup);
  });
  mainGroup.add(group);
}

function updateNode(
  data: SeriesData,
  dataIndex: number,
  seriesModel: RelationshipDiagramSeriesModel,
  currentNodeGroup: graphic.Group[],
  mainGroup: graphic.Group
) {
  const { layoutInfo } = seriesModel;
  const group = new graphic.Group({ name: 'trees', x: layoutInfo.x, y: layoutInfo.y });
  const node = data.tree.getNodeByDataIndex(dataIndex) as unknown as RelationTreeNode;
  const nodeInfo = node.getLayout() as BoundingRect;
  // 深度为0和1的节点只做为定位计算使用，不参与视图绘制
  if (!nodeInfo || node.depth < 2) {
    return;
  }
  data.setItemGraphicEl(dataIndex, node.nodeElGroup as unknown as Group);
  group.add(node.nodeElGroup as unknown as Group);
  if (node.lineElGroup) {
    group.add(node.lineElGroup as unknown as Group);
  }
  mainGroup.add(group);
  // 缓存节点元素
  currentNodeGroup.push(node.nodeElGroup as unknown as Group);
}

class RelationshipDiagramView extends ChartView {
  static readonly type = 'dvRelationshipDiagram';

  readonly type = RelationshipDiagramView.type;

  private __mainGroup = new graphic.Group({ name: 'mainGroup' });

  private __controller: RoamController;

  controllerHost: RoamControllerHost;

  private __data: SeriesData;

  /** 是否是首次渲染 */
  private __initRendered: boolean;

  /** 初始渲染时，内容的最大宽度（用于展开不同列节点时的缩放倍数限制） */
  private __initTreeWidth: number;

  /** 存放所有节点的的图形元素，用于性能优化 */
  private __currentNodeGroup: Group[];

  init(ecModel: GlobalModel, api: ExtensionAPI) {
    this.__controller = new RoamController(api.getZr());

    this.controllerHost = {
      target: this.group
    } as RoamControllerHost;

    this.__currentNodeGroup = [];

    this.__initRendered = true;

    this.__initTreeWidth = 0;

    this.group.add(this.__mainGroup);
  }

  render(
    this: RelationshipDiagramViewClazz,
    seriesModel: RelationshipDiagramSeriesModel,
    ecModel: GlobalModel,
    api: ExtensionAPI,
    payload: Payload
  ) {
    const { trees } = seriesModel;
    this.__data = seriesModel.trees[0].data;
    const mainGroup = this.__mainGroup;
    // ! 重绘前要先把上一次绘制的图形清楚了，不然会重叠
    mainGroup.removeAll();

    trees.forEach((tree: Tree) => {
      const { data } = tree;
      // 每个节点的绘制
      data
        .diff(null)
        .add(newIdx => {
          updateNode(data, newIdx, seriesModel, this.__currentNodeGroup, mainGroup);
        })
        .execute();

      // 绘制背景、水印、导向线、分支线
      drawBackgroundColor(mainGroup, tree);
      drawWatermark(mainGroup, tree);
      drawToNextTreeLine(mainGroup, tree);
      drawLevelLine(mainGroup, tree);

      // 渲染后的事件响应
      getInstanceById(api.getId()).trigger('dvafterrender', {
        type: 'dvafterrender',
        payload
      });

      this._initRendered(seriesModel, ecModel, api);
    });
  }

  _initRendered(
    seriesModel: RelationshipDiagramSeriesModel,
    ecModel: GlobalModel,
    api: ExtensionAPI
  ) {
    if (this.__initRendered) {
      this.__initRendered = false;
      // 计算初始的树宽度（可用于缩放限制）
      const dvBackgroundPadding = seriesModel.option?.dvBackground?.padding || [0, 0, 0, 0];
      this.__initTreeWidth = Math.max(...seriesModel.treeMaxX) + dvBackgroundPadding[1];
      // 首屏自适应
      this.adapt(seriesModel, ecModel, api);
    }
  }

  /**
   * 首屏自适应，默认开启，如果开启的话，会在第一次渲染时根据树的最大宽度自动缩放
   */
  adapt(seriesModel: RelationshipDiagramSeriesModel, ecModel: GlobalModel, api: ExtensionAPI) {
    const viewRect = { x: 0, y: 0, width: api.getWidth(), height: api.getHeight() };
    const rect = this.group.getBoundingRect().clone();
    rect.applyTransform(this.group.transform);

    const romaComponentModel = ecModel.getComponent('roam');
    if (!romaComponentModel) {
      return;
    }
    const romaComponentView = api.getViewOfComponentModel(
      romaComponentModel
    ) as unknown as RoamView;
    const roamState = romaComponentView.getSeriesViewState(seriesModel.seriesIndex);

    // ! 先去掉限制
    romaComponentView.setSeriesViewState(seriesModel.seriesIndex, {
      zoomLimit: { min: 0, max: Infinity }
    });

    /** 自适应比例 */
    let zoomDelta: number;
    const { dvAdapt } = seriesModel.option;
    // 关闭自适应
    if (!dvAdapt) {
      zoomDelta = 1;
    } else {
      // 自适应比例
      zoomDelta = Math.max(viewRect.width / rect.width, viewRect.height / rect.height);
    }

    romaComponentView.updateSeriesViewOnPanAndZoom(
      seriesModel.seriesIndex,
      {
        dx: viewRect.x - rect.x,
        dy: viewRect.y - rect.y,
        zoomDelta,
        zoomX: 0,
        zoomY: 0
      },
      false,
      false
    );

    // 最小的缩小倍数
    roamState.zoomLimit.min = 1;

    // 重置为默认状态
    romaComponentView.setSeriesViewState(seriesModel.seriesIndex, roamState);
  }
}

export default RelationshipDiagramView;
